<!-- Generic ticket view -->
<template lang="pug">
.ticket-preview(
  v-if="missingTicketPropertyCustomIds.length === 0",
  ref="ticketRef",
  :class="{ [ticketWideClass]: isWide }"
)
  .flex.justify-between.items-center.mb-8.ticket-title-row
    .flex.items-center(v-if="props.preview")
      EditableText.ticket-title(
        :content="props.ticket.name",
        :save-callback="setTwinName",
        :disabled="!isTitleDescriptionEditable"
      )
        router-link.ticket-title(
          :to="{ name: ticketDetailLink.routeName, params: { template: props.ticket.templateUuid, id: props.ticket.uuid } }"
        )
          .flex.items-center
            h1 {{ props.ticket.name }}
            el-icon.ml-2
              Link
    EditableText.ticket-title(
      v-else,
      :content="props.ticket.name",
      :save-callback="setTwinName",
      :disabled="!isTitleDescriptionEditable"
    )
      h1 {{ props.ticket.name }}

    router-link(
      :to="{ name: props.ticketHistoryViewName ? props.ticketHistoryViewName : 'ticketHistory', params: { id: props.ticket.uuid }, query: { last: route?.query?.last } }"
    )
      el-button(link, type="primary", icon="Link") {{ $t("ticketHistory") }}
  .ticket-metadata
    .tm-item(v-if="isPropertyVisible(ticketProps.ticketNumber)")
      .smx-label {{ $t("ticketNumber") }}
      EditableText.min-w-0(
        :content="ticketNumber",
        :save-callback="saveTicketNumber",
        :disabled="!isTicketNumberEditable"
      )
        span.block.truncate(:title="ticketNumber") {{ ticketNumber }}

    slot(name="additionalHeaderFields")

    .tm-item(v-if="isPropertyVisible(ticketProps.status)")
      .smx-label {{ $t("status") }}
      StatusTag.w-fit(:status="status")

    .tm-item(
      v-if="isPropertyVisible(ticketProps.priority)",
      :class="{ 'cursor-pointer': isPriorityEditable }",
      @click="showPrioritySelect"
    )
      .smx-label {{ $t("priority") }}
      .prioritySelect.flex
        PrioritySelect(
          ref="prioritySelect",
          v-model="priority",
          :disabled="isPropertyLocked(ticketProps.priority)",
          style="max-width: 0; max-height: 0; overflow: hidden",
          @click.stop
        )
        PriorityTag(:priority="priority")
      el-icon.edit-icon(v-if="isPriorityEditable")
        Edit

    .tm-item(
      v-if="isPropertyVisible(ticketProps.actualMaintenanceTime) && workDurationProperty"
    )
      .smx-label {{ $t("workDuration") }}
      .flex.items-center
        .flex.flex-col
          TwinPropertyInput(
            v-if="workDurationProperty && workDurationEditMode",
            v-model="workDuration",
            :disabled="isPropertyLocked(ticketProps.actualMaintenanceTime)",
            :prop="getWorkDurationProperty()"
          )
          TwinPropertyView(
            v-else,
            :prop="getWorkDurationProperty()",
            :model-value="workDuration"
          )
            //- v-loading="loading",
          .flex.flex-row.mt-4(v-if="workDurationEditMode")
            el-button.mr-1(
              v-loading="workDurationLoading",
              type="primary",
              size="small",
              @click="saveWorkDuration"
            ) {{ $t("save") }}
            el-button(size="small", @click="cancelWorkDurationEdit") {{ $t("cancel") }}

        el-icon.edit-icon.cursor-pointer.ml-4(
          v-if="isWorkDurationEditable && !workDurationEditMode",
          @click="workDurationEditMode = true"
        )
          Edit

    template(
      v-if="isPropertyVisible(ticketProps.ticketOrigin) && ticketOriginProp && ticketOriginValue !== undefined"
    )
      .tm-item
        .smx-label {{ $t("ticketOrigin") }}
        TwinPropertyView(
          :prop="ticketOriginProp",
          :model-value="ticketOriginValue"
        )

    .tm-item-boxes
      .tm-item(
        v-if="isPropertyVisible(ticketProps.startDate)",
        :class="{ 'cursor-pointer': isStartDateEditable }",
        @click="showStartDatePicker"
      )
        .smx-label {{ $t("plannedStart") }}
        .flex
          span {{ formatDate(startDate) }}
          el-date-picker(
            ref="startDatePicker",
            v-model="startDate",
            :disabled="isPropertyLocked(ticketProps.startDate)",
            style="max-width: 0; max-height: 0; overflow: hidden",
            format="DD/MM/YYYY",
            value-format="YYYY-MM-DD"
          )
        el-icon(v-if="isStartDateEditable")
          Edit

      //- Due date
      .tm-item(
        v-if="isPropertyVisible(ticketProps.dueDate)",
        :class="{ 'cursor-pointer': isDueDateEditable }",
        @click="showDueDatePicker"
      )
        .smx-label {{ $t("plannedFinish") }}
        .flex
          el-date-picker(
            ref="dueDatePicker",
            v-model="dueDate",
            :disabled="isPropertyLocked(ticketProps.dueDate)",
            style="max-width: 0; max-height: 0; overflow: hidden",
            format="DD/MM/YYYY",
            value-format="YYYY-MM-DD"
          )
          span.mr-4 {{ formatDate(dueDate) }}
        el-icon(v-if="isDueDateEditable")
          Edit

    //- Assigned users 
    el-popover(
      v-if="isPropertyVisible(ticketProps.assignee)",
      :visible="assigneePopoverVisible",
      :width="300",
      :disabled="!isAssigneeEditable"
    )
      .flex.flex-row.justify-end.mb-4.cursor-pointer
        el-icon(@click="assigneePopoverVisible = false") 
          Close
      UserSelect(v-model="assignees", v-loading="assigneeLoading")
      .flex.flex-row.justify-end
        el-button.mt-4(type="primary", @click="assigneePopoverVisible = false") {{ $t("close") }}
      template(#reference)
        .tm-item(
          :class="{ 'cursor-pointer': isAssigneeEditable }",
          @click="showAssigneePopover"
        )
          .smx-label {{ $t("assignee") }}
          UsersField(
            :user-ids="assignees",
            show-avatar,
            type="stack",
            max-width="100%"
          )
          el-icon(v-if="isAssigneeEditable")
            Edit

    el-popover(
      v-if="isPropertyVisible(ticketProps.contributors)",
      :visible="contributorsPopoverVisible",
      :width="300",
      :disabled="!isContributorsEditable"
    )
      .flex.flex-row.justify-end.mb-4.cursor-pointer
        el-icon(@click="contributorsPopoverVisible = false") 
          Close
      UserSelect(
        v-model="contributors",
        v-loading="contributorsLoading",
        multiple
      )
      .flex.flex-row.justify-end
        el-button.mt-4(
          type="primary",
          @click="contributorsPopoverVisible = false"
        ) {{ $t("close") }}
      template(#reference)
        .tm-item(
          :class="{ 'cursor-pointer': isContributorsEditable }",
          @click="showContributorsPopover"
        )
          .smx-label {{ $t("contributors") }}
          .flex.items-center.min-w-0(v-if="contributors.length > 0")
            UsersField(
              :user-ids="firstContributor",
              show-avatar,
              :max-width="contributors.length > 1 ? 'calc(100% - 80px)' : '100%'"
            )
            span.ml-4.flex-none.text-slate-400(v-if="contributors.length > 1") {{ $t("andNMore", { n: contributors.length - 1 }) }}

          span(v-else) -
          el-icon(v-if="isContributorsEditable")
            Edit

    .tm-item(v-if="isPropertyVisible(ticketProps.requester)")
      .smx-label {{ $t("reportedBy") }}
      UsersField(:user-ids="requester", show-avatar, max-width="100%")

    //- If the requester is not the creator we also show the creator, see https://github.com/inctec/smx-portal/issues/203
    template(
      v-if="isPropertyVisible(ticketProps.createdBy) && requester[0] !== createdBy[0]"
    )
      .tm-item
        .smx-label {{ $t("createdBy") }}
        UsersField(:user-ids="createdBy", show-avatar, max-width="100%")

      .tm-item.placeholder

    .tm-item-boxes
      .tm-item
        .smx-label {{ $t("createdAt") }}
        span {{ formatDateTime(ticket.created) }}

      .tm-item
        .smx-label {{ $t("lastModified") }}
        span {{ formatDateTime(ticket.modified) }}

    template(v-if="maintenancePlan")
      .tm-item
        .smx-label {{ $t("maintenancePlan") }}
        .link.min-w-0(v-if="canLinkToMaintenancePlan")
          router-link.block.truncate(
            :title="maintenancePlan.name",
            :to="{ name: 'tmMaintenancePlansEdit', params: { id: maintenancePlan.uuid } }"
          ) {{ maintenancePlan.name }}
        span.block.truncate(v-else, :title="maintenancePlan.name") {{ maintenancePlan.name }}

    .tm-item-boxes
      //- Documents
      .tm-item-box
        TwinDocumentsBox(
          :twin-id="props.ticket.uuid",
          :disabled="!isDocumentsEditable",
          max-width="100%"
        )

      //- Twin relations
      .tm-item-box
        TwinRelationsBox(
          :twin="props.ticket",
          :disabled="!isTwinRelationsEditable",
          :query="{}",
          max-width="100%"
        )

  .ticket-content.mt-8
    .flex.items-center
      h1.mr-4 {{ propValuesByCustomId[ticketProps.maintenanceRequestType] === "damageNotice" ? $t("damageDescription") : $t("description") }}
      el-icon.cursor-pointer(
        v-if="isDecriptionEditable",
        @click="isDescriptionEditMode = true"
      )
        Edit
    .ticket-detail.mb-8
      .description(v-if="isDescriptionEditMode")
        el-input.mb-4(v-model="description", type="textarea", :rows="5")
        .flex.justify-end
          el-button.mr-2(
            type="danger",
            size="small",
            @click.stop="cancelDescription"
          ) {{ $t("cancel") }}
          el-button(
            type="primary",
            size="small",
            @click.stop="saveDescription"
          ) {{ $t("save") }}
      .description.whitespace-pre-wrap.min-h-36(v-else) {{ props.ticket.description }}

    h1.mr-4 {{ $t("images") }}
    .ticket-detail.mb-8.flex.justify-center
      TwinImages(
        :twin-id="props.ticket.uuid",
        :disabled="!isDocumentsEditable"
      )

    .ticket-tasklist.mb-8
      TasklistAction(
        :ticket="ticket",
        :useAuth="props.useAuth",
        :ticket-template-query="props.ticketTemplateQuery"
      )

    .ticket-sop.mb-8(v-if="!props.hideSops")
      SopAction(:ticket="ticket", :useAuth="props.useAuth")

    slot(name="additionalFields")

    .flex.items-center(v-if="isPropertyVisible(ticketProps.workCarriedOut)")
      h1.mr-4 {{ $t("workCarriedOut") }}
      el-icon.cursor-pointer(
        v-if="isWorkCarriedOutEditable",
        @click="isWorkCarriedOutEditMode = true"
      )
        Edit
    .ticket-detail.mb-8(v-if="isPropertyVisible(ticketProps.workCarriedOut)")
      .description(v-if="isWorkCarriedOutEditMode")
        el-input.mb-4(v-model="workCarriedOut", type="textarea", :rows="5")
        .flex.justify-end
          el-button.mr-2(
            type="danger",
            size="small",
            @click="cancelWorkCarriedOut"
          ) {{ $t("cancel") }}
          el-button(type="primary", size="small", @click="saveWorkCarriedOut") {{ $t("save") }}
      .description.whitespace-pre-wrap.min-h-36(v-else) {{ propValuesByCustomId.workCarriedOut }}

  .ticket-actions.mb-8
    TicketActionButton(
      :ticket="props.ticket",
      :useAuth="props.useAuth",
      :work-carried-out="workCarriedOut",
      :work-duration="workDuration",
      :work-duration-property="workDurationProperty",
      @edit="editAction"
    )

  .ticket-comments
    Comments(:key="commentKey", :twin-id="ticket.uuid")

el-alert(
  v-else,
  type="warning",
  :title="$t('systemMisconfigured')",
  :closable="false",
  show-icon
)
  p {{ $t("ticketViewMissingCustomIds") }}
  ul.list-disc.pl-6
    li(v-for="customId in missingTicketPropertyCustomIds", :key="customId") {{ customId }}
</template>

<script setup lang="ts">
import Comments from "@shared/components/comments/Comments.vue";
import {
  type TwinProperty,
  type TwinResponse,
} from "../../services/twinManagement";
import UsersField from "@shared/components/twinManagement/components/UsersField.vue";
import { formatDate, formatDateTime } from "@shared/util/formatting";
import {
  computed,
  type DefineComponent,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import PriorityTag from "./components/PriorityTag.vue";
import StatusTag from "./components/StatusTag.vue";
import EditableText from "@shared/components/elements/EditableText.vue";
import { useI18n } from "vue-i18n";
import { useTwinStore } from "../../stores/twinManagement";
import UserSelect from "@shared/components/twinManagement/components/UserSelect.vue";
import TwinDocumentsBox from "@shared/components/elements/TwinDocumentsBox.vue";
import TwinRelationsBox from "@shared/components/twinManagement/components/TwinRelationsBox.vue";
import TicketActionButton from "./components/TicketActionButton.vue";
import { useAuthStore } from "../../stores/auth";
import PrioritySelect from "./components/PrioritySelect.vue";
import { statuses, ticketProps } from "./constants";
import SopAction from "@shared/components/tickets/components/SopAction.vue";
import TasklistAction from "@shared/components/tickets/components/TasklistAction.vue";
import TwinPropertyInput from "@shared/components/twinManagement/components/TwinPropertyInput.vue";
import TwinImages from "@shared/components/elements/TwinImages.vue";
import { showResponseErrorToUser } from "@shared/util/genericResponseErrors";
import {
  getMaintenanceRules,
  getTicketRecurrences,
  type MaintenanceRuleResponse,
} from "@shared/maintenance/service";
import { useRoute } from "vue-router";
import { type TicketManagementAuth } from "@shared/maintenance/hooks/maintenanceAuth";
import { type TwinManagementTypes } from "@inctec/smx-ui-client";
import { isNil } from "lodash";
import TwinPropertyView from "../twinManagement/components/TwinPropertyView.vue";
import { useAppStore } from "../../stores/apps";

export interface TicketPropertyRestriction {
  hidden?: boolean;
  locked?: boolean;
}

const props = defineProps<{
  ticket: TwinResponse;
  useAuth: () => TicketManagementAuth;
  hideSops?: boolean;
  ticketTemplateQuery?: Omit<
    TwinManagementTypes.SearchTwinsData["body"],
    "isTemplate" | "active" | "showProperties"
  >;
  preview?: boolean;
  propertyRestrictions?: Record<string, TicketPropertyRestriction>;
  // Router name of the view for ticket history, this is usually a twin LifecycleEvents view
  ticketHistoryViewName?: string;
}>();

const { t } = useI18n();

const emit = defineEmits(["edit"]);
const ticketAuth = props.useAuth();
const authStore = useAuthStore();
const appStore = useAppStore();
const ticketDetailLink = computed(() => appStore.getTwinEditLink(props.ticket));

const prioritySelect = ref<DefineComponent | null>(null);

const isAdmin = computed(() => {
  return ticketAuth.isAdmin();
});

const isPlanner = computed(() => {
  return ticketAuth.isPlanner(props.ticket);
});

function isPropertyVisible(customId: string) {
  return !props.propertyRestrictions?.[customId]?.hidden;
}

function isPropertyLocked(customId: string) {
  return Boolean(props.propertyRestrictions?.[customId]?.locked);
}

function showPrioritySelect() {
  if (prioritySelect.value && isPriorityEditable.value) {
    // TODO: this is unfortunate since the element plus API might change at any time
    prioritySelect.value.$el.firstElementChild.click();
  }
}

const commentKey = ref(0);

function editAction() {
  emit("edit");
  commentKey.value = commentKey.value + (1 % 9);
}

const priority = computed({
  get() {
    return propValuesByCustomId.value["priority"] as number | undefined;
  },
  set(val) {
    if (isPropertyLocked(ticketProps.priority)) {
      return;
    }
    const uuid = props.ticket.properties!.find(
      (item) => item.customId === "priority",
    )?.uuid;
    if (isNil(uuid)) {
      return;
    }
    emit("edit");
    void twinStore.editTwin(props.ticket, {
      properties: [
        {
          propertyUuid: uuid,
          value: val as number,
        },
      ],
    });
  },
});

const propValuesByCustomId = computed(() => {
  const o: Record<string, unknown> = {};
  props.ticket.properties?.forEach((prop) => {
    o[prop.customId!] = prop.propertyValue?.value;
  });
  return o;
});

const ticketNumber = computed(() => {
  const value = propValuesByCustomId.value[ticketProps.ticketNumber];
  return typeof value === "string" ? value : "";
});

const requester = computed(
  () =>
    (propValuesByCustomId.value[ticketProps.requester] as
      | string[]
      | undefined) ?? [],
);

const createdBy = computed(
  () =>
    (propValuesByCustomId.value[ticketProps.createdBy] as
      | string[]
      | undefined) ?? [],
);

const ticketOriginProp = computed(() => {
  return props.ticket.properties?.find(
    (item) => item.customId === ticketProps.ticketOrigin,
  );
});

const ticketOriginValue = computed(() => {
  return propValuesByCustomId.value[ticketProps.ticketOrigin];
});

const missingTicketPropertyCustomIds = computed(() => {
  const ticketPropertyCustomIds = new Set(
    props.ticket.properties?.map((property) => property.customId),
  );
  return Object.values(ticketProps).filter(
    (customId) => !ticketPropertyCustomIds.has(customId),
  );
});

// Editing
const twinStore = useTwinStore();

async function setTwinName(name: string) {
  await twinStore.patchAssetTwin(props.ticket.uuid, { name });
}

const isAssignee = computed(() => {
  if (authStore.userId === null) {
    return false;
  }
  return assignees.value?.includes(authStore.userId);
});

const isCreator = computed(() => {
  return (
    (
      props.ticket.properties?.find((item) => item.customId === "createdBy")
        ?.propertyValue?.value as string[] | undefined
    )?.[0] === authStore.userId
  );
});

// TODO: Refactor status into hook, as it is used amongst multiple ticket features

const status = computed(() => {
  return (
    (props.ticket.properties?.find((item) => item.customId === "status")
      ?.propertyValue?.value as string | undefined) ?? statuses.new
  );
});

const isBeforeCompleted = computed(() => {
  if (status.value === undefined) {
    return false;
  }
  return [
    statuses.draft,
    statuses.planned,
    statuses.new,
    statuses.inProcess,
  ].includes(status.value);
});
// const isBeforeProcessing = computed(() => {
//   return [statuses.draft, statuses.planned, statuses.new].includes(status.value)
// })

const isTitleDescriptionEditable = computed(() => {
  return (isCreator.value || isAdmin.value) && isBeforeCompleted.value;
});

const isTicketProcessor = computed(() => {
  return ticketAuth.isTicketTypeProcessor(props.ticket);
});

const isTicketManager = computed(() => {
  return ticketAuth.isTicketTypeManager(props.ticket);
});

const isPriorityAndDatesEditable = computed(() => {
  return (
    (isAdmin.value || isTicketManager.value || isTicketProcessor.value) &&
    status.value !== statuses.completed &&
    status.value !== statuses.rejected &&
    status.value !== statuses.confirmed
  );
});

const isPriorityEditable = computed(
  () =>
    isPriorityAndDatesEditable.value && !isPropertyLocked(ticketProps.priority),
);

const isDatesEditable = computed(
  () =>
    isPriorityAndDatesEditable.value &&
    status.value !== statuses.completed &&
    status.value !== statuses.rejected &&
    status.value !== statuses.confirmed,
);

const isStartDateEditable = computed(
  () => isDatesEditable.value && !isPropertyLocked(ticketProps.startDate),
);

const isDueDateEditable = computed(
  () => isDatesEditable.value && !isPropertyLocked(ticketProps.dueDate),
);
// damage description

const isDescriptionEditMode = ref(false);

const _description = ref<null | string>(null);

const description = computed({
  get() {
    return _description.value !== null
      ? _description.value
      : props.ticket.description;
  },
  set(value) {
    _description.value = value;
  },
});

async function saveDescription() {
  await twinStore.patchAssetTwin(props.ticket.uuid, {
    description: description.value,
  });
  isDescriptionEditMode.value = false;
}
function cancelDescription() {
  isDescriptionEditMode.value = false;
  _description.value = null;
}

// work carried out

const isWorkCarriedOutEditable = computed(() => {
  return (
    (isAdmin.value || (isAssignee.value && status.value === "in process")) &&
    !isPropertyLocked(ticketProps.workCarriedOut) &&
    isBeforeCompleted.value
  );
});

const isDecriptionEditable = computed(() => {
  return isAdmin.value && isBeforeCompleted.value;
});

const isWorkCarriedOutEditMode = ref(false);

const _workCarriedOut = ref<string | null>(null);

const workCarriedOut = computed({
  get() {
    return _workCarriedOut.value !== null
      ? _workCarriedOut.value
      : (propValuesByCustomId.value[ticketProps.workCarriedOut] as string);
  },
  set(value) {
    _workCarriedOut.value = value;
  },
});

async function saveWorkCarriedOut() {
  if (isPropertyLocked(ticketProps.workCarriedOut)) {
    return;
  }
  const uuid = props.ticket.properties!.find(
    (item) => item.customId === ticketProps.workCarriedOut,
  )?.uuid;
  emit("edit");
  if (uuid === undefined) {
    throw new Error("UUID for workCarriedOut property not found");
  }
  await twinStore.editTwin(props.ticket, {
    properties: [
      {
        propertyUuid: uuid,
        value: workCarriedOut.value,
      },
    ],
  });

  isWorkCarriedOutEditMode.value = false;
}

async function saveTicketNumber(value: string) {
  if (isPropertyLocked(ticketProps.ticketNumber)) {
    return;
  }
  const propertyUuid = props.ticket.properties!.find(
    (item) => item.customId === ticketProps.ticketNumber,
  )?.uuid;
  if (propertyUuid === undefined) {
    throw new Error("UUID for ticketNumber property not found");
  }
  await twinStore.editTwin(props.ticket, {
    properties: [
      {
        propertyUuid,
        value,
      },
    ],
  });
}

function cancelWorkCarriedOut() {
  isWorkCarriedOutEditMode.value = false;
  _workCarriedOut.value = null;
}

// contributors
const contributorsPopoverVisible = ref(false);

function showContributorsPopover() {
  if (isAssigneeEditable.value) {
    contributorsPopoverVisible.value = true;
  }
}

const contributorsLoading = ref(false);

const contributors = computed({
  get() {
    return (
      (propValuesByCustomId.value[ticketProps.contributors] as Array<string>) ??
      []
    );
  },
  set(ids) {
    if (isPropertyLocked(ticketProps.contributors)) {
      return;
    }
    const uuid = props.ticket.properties!.find(
      (item) => item.customId === ticketProps.contributors,
    )?.uuid;
    contributorsLoading.value = true;
    emit("edit");
    if (uuid === undefined) {
      throw new Error("UUID for contributors property not found");
    }
    void twinStore
      .editTwin(props.ticket, {
        properties: [
          {
            propertyUuid: uuid,
            value: ids,
          },
        ],
      })
      .finally(() => {
        contributorsLoading.value = false;
      });
  },
});

const firstContributor = computed(() => contributors.value.slice(0, 1));

// assignee
const assigneePopoverVisible = ref(false);

function showAssigneePopover() {
  if (isAssigneeEditable.value) {
    assigneePopoverVisible.value = true;
  }
}

const assigneeLoading = ref(false);

const assignees = computed({
  get() {
    return (
      (propValuesByCustomId.value["assignee"] as string[] | undefined) ?? []
    );
  },
  set(ids) {
    if (isPropertyLocked(ticketProps.assignee)) {
      return;
    }
    const uuid = props.ticket.properties!.find(
      (item) => item.customId === "assignee",
    )?.uuid;
    assigneeLoading.value = true;
    emit("edit");
    if (uuid === undefined) {
      throw new Error("UUID for assignee property not found");
    }
    void twinStore
      .editTwin(props.ticket, {
        properties: [
          {
            propertyUuid: uuid,
            value: ids,
          },
        ],
      })
      .finally(() => {
        assigneeLoading.value = false;
      });
  },
});

const startDatePicker = ref<DefineComponent | null>(null);

function showStartDatePicker() {
  if (isStartDateEditable.value && startDatePicker.value) {
    startDatePicker.value.handleOpen();
  }
}

const startDate = computed({
  get() {
    return propValuesByCustomId.value["startDate"] as string | undefined;
  },
  set(date: string) {
    if (isPropertyLocked(ticketProps.startDate)) {
      return;
    }
    const uuid = props.ticket.properties!.find(
      (item) => item.customId === "startDate",
    )?.uuid;
    if (uuid === undefined) {
      throw new Error("UUID for startDate property not found");
    }
    emit("edit");
    void twinStore.editTwin(props.ticket, {
      properties: [
        {
          propertyUuid: uuid,
          value: date,
        },
      ],
    });
  },
});

const dueDatePicker = ref<DefineComponent | null>(null);
function showDueDatePicker() {
  if (isDueDateEditable.value && dueDatePicker.value) {
    dueDatePicker.value.handleOpen();
  }
}

const dueDate = computed({
  get() {
    return propValuesByCustomId.value["dueDate"] as string | undefined;
  },
  set(date: string) {
    if (isPropertyLocked(ticketProps.dueDate)) {
      return;
    }
    const uuid = props.ticket.properties!.find(
      (item) => item.customId === "dueDate",
    )?.uuid;
    if (uuid === undefined) {
      throw new Error("UUID for dueDate property not found");
    }
    void twinStore.editTwin(props.ticket, {
      properties: [
        {
          propertyUuid: uuid,
          value: date,
        },
      ],
    });
    emit("edit");
  },
});

const _workDuration = ref<string | null>(null);

const workDurationProperty = computed(() =>
  props.ticket.properties?.find(
    (item) => item.customId === ticketProps.actualMaintenanceTime,
  ),
);

function getWorkDurationProperty(): TwinProperty {
  if (workDurationProperty.value === undefined) {
    throw new Error("Work duration property not found");
  }
  return workDurationProperty.value;
}

const workDuration = computed({
  get() {
    return _workDuration.value !== null
      ? _workDuration.value
      : (propValuesByCustomId.value[ticketProps.actualMaintenanceTime] as
          | string
          | undefined);
  },
  set(value) {
    if (
      _workDuration.value !== null ||
      propValuesByCustomId.value[ticketProps.actualMaintenanceTime] !== value
    ) {
      _workDuration.value = value ?? null;
    }
  },
});

const workDurationEditMode = ref(false);

const isWorkDurationEditable = computed(
  () =>
    !isPropertyLocked(ticketProps.actualMaintenanceTime) &&
    ![statuses.completed, statuses.confirmed].includes(status.value ?? ""),
);

function cancelWorkDurationEdit() {
  _workDuration.value = null;
  workDurationEditMode.value = false;
}

const workDurationLoading = ref(false);

async function saveWorkDuration() {
  if (isPropertyLocked(ticketProps.actualMaintenanceTime)) {
    return;
  }
  workDurationLoading.value = true;
  const uuid = props.ticket.properties!.find(
    (item) => item.customId === ticketProps.actualMaintenanceTime,
  )?.uuid;
  if (uuid === undefined) {
    throw new Error("UUID for workDuration property not found");
  }
  await twinStore
    .editTwin(props.ticket, {
      properties: [
        {
          propertyUuid: uuid,
          value: workDuration.value ?? "",
        },
      ],
    })
    .then(() => {
      _workDuration.value = null;
    })
    .catch((e) => {
      showResponseErrorToUser(e, t);
      throw e;
    })
    .finally(() => {
      workDurationLoading.value = false;
    });

  workDurationEditMode.value = false;
  emit("edit");
}

const isDocumentsEditable = computed(
  () =>
    isAdmin.value ||
    ![statuses.completed, statuses.confirmed].includes(status.value),
);
const isTwinRelationsEditable = computed(
  () =>
    (isAdmin.value || isPlanner) &&
    ![statuses.completed, statuses.confirmed].includes(status.value),
);
const isTicketNumberEditable = computed(
  () =>
    isAdmin.value &&
    !isPropertyLocked(ticketProps.ticketNumber) &&
    ![statuses.completed, statuses.confirmed].includes(status.value),
);

const isAssigneeEditable = computed(() => {
  return !isPropertyLocked(ticketProps.assignee) && canEditAssignees.value;
});

const canEditAssignees = computed(() => {
  if (!isBeforeCompleted.value) {
    return false;
  }
  if (isAdmin.value) {
    return true;
  }
  if (isPlanner.value || isAssignee.value) {
    return [statuses.new, statuses.planned, statuses.inProcess].includes(
      status.value,
    );
  }
  return false;
});

const isContributorsEditable = computed(() => {
  return !isPropertyLocked(ticketProps.contributors) && canEditAssignees.value;
});

// Show maintenance plan if it exists #789

const maintenancePlan = ref<MaintenanceRuleResponse | null>(null);

async function fetchMaintenancePlan() {
  const ticketRecurrence = await getTicketRecurrences({
    ticketUuid: props.ticket.uuid,
    page: 1,
    pageSize: 1,
  });
  const recurrence = ticketRecurrence.data.result[0];
  if (recurrence !== undefined) {
    const response = await getMaintenanceRules({
      versionUuid: recurrence.ruleVersion,
      page: 1,
      pageSize: 1,
    });

    maintenancePlan.value = response.data?.result?.[0] ?? null;
  }
}

const ticketRef = ref<HTMLElement | null>(null);
const isWide = ref(false);
const ticketWideClass = "ticket-wide";
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  void fetchMaintenancePlan();
  resizeObserver = new ResizeObserver((entries) => {
    // NOTE: collapsing has an issue where the metadata collapses but not the grid layout,
    // we disable large screen layout for now until we can fix this issue
    // isWide.value = (entries[0]?.contentRect.width ?? 0) >= 1024;
  });
  if (ticketRef.value) {
    resizeObserver.observe(ticketRef.value);
  }
});

onUnmounted(() => {
  resizeObserver?.disconnect();
});

const route = useRoute();

// Maintenance plan link visibility based on roles
// Admin and planner can see and click the link, user and ticket-creator can only see the name
const canLinkToMaintenancePlan = computed(() => {
  return isAdmin.value || isPlanner.value;
});
</script>

<style scoped>
.ticket-content {
  grid-area: content;
  min-width: 0 !important;
}

.ticket-actions {
  grid-area: actions;
}

.ticket-metadata {
  display: grid;
  grid-area: metadata;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  grid-gap: 0.7rem 2rem;
  align-items: start;
  justify-content: center;
  align-self: start;
}

.ticket-detail {
  grid-area: detail;
  background-color: white;
  border-radius: 1rem;
  padding: 1rem;
}

.ticket-title-row {
  grid-area: title;
}

.ticket-comments {
  grid-area: comments;
}

.ticket-preview {
  max-width: 2048px;
  display: grid;
  grid-template-columns: 1fr;
  grid-template-areas: "title" "metadata" "content" "actions" "comments";
}

.ticket-title {
  grid-area: title;
}

:deep(.tm-item) {
  display: grid;
  grid-gap: 1rem;
  min-height: 32px;
  min-width: 0;
  align-items: center;
  grid-template-columns: max-content minmax(0, 1fr) max-content;
  grid-template-areas: "label content edit";
}

.tm-item-boxes {
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
}

.tm-item-box {
  min-width: 0;
}

.ticket-preview.ticket-wide {
  grid-template-columns: 1fr 320px;
  column-gap: 2rem;
  grid-template-areas:
    "title    title"
    "content  metadata"
    "actions  metadata"
    "comments metadata";
}

.ticket-preview.ticket-wide .ticket-metadata {
  position: sticky;
  top: 1rem;
  grid-template-columns: 1fr;
}
</style>
